import type { ActiveControlSchemeV1, ControlSchemePreferenceV1, LastControlInputV1 } from "@gordonland/ui";
import { PLACEABLE_BLOCK_IDS_V1, type PlaceableBlockId } from "@gordonland/world-format";

import type { P0020PlayableRuntimeV1 } from "./p0-020-playable.js";

const BLOCK_BY_CODE: Readonly<Record<string, PlaceableBlockId | undefined>> = Object.freeze({
  Digit0: 10,
  Digit1: 1,
  Digit2: 2,
  Digit3: 3,
  Digit4: 4,
  Digit5: 5,
  Digit6: 6,
  Digit7: 7,
  Digit8: 8,
  Digit9: 9,
  Equal: 12,
  Minus: 11,
});
const MOVEMENT_CODES = Object.freeze(new Set([
  "ArrowDown", "ArrowLeft", "ArrowRight", "ArrowUp", "KeyA", "KeyD", "KeyS", "KeyW",
]));
const MODIFIER_CODES = Object.freeze(new Set([
  "AltLeft", "AltRight", "ControlLeft", "ControlRight", "MetaLeft", "MetaRight", "ShiftLeft", "ShiftRight",
]));

interface PointV1 {
  readonly x: number;
  readonly y: number;
}

interface MouseDragV1 {
  readonly button: number;
  readonly pointerId: number;
  dragged: boolean;
  x: number;
  y: number;
}

export interface GameInputStateV1 {
  readonly activeScheme: ActiveControlSchemeV1;
  readonly blocked: boolean;
  readonly preference: ControlSchemePreferenceV1;
}

export interface GameInputSnapshotV1 {
  readonly consumedKeyboardSequences: number;
  readonly consumedPointerSequences: number;
  readonly lookPointers: number;
  readonly movePointers: number;
  readonly pointerLocked: boolean;
}

export interface GameInputControllerV1 {
  readonly moveRegionHandlers: TouchRegionHandlersV1;
  readonly lookRegionHandlers: TouchRegionHandlersV1;
  requestPointerLock(): Promise<boolean>;
  reset(reason: string): void;
  setSprintLatched(enabled: boolean): void;
  snapshot(): GameInputSnapshotV1;
  dispose(): void;
}

export interface TouchRegionHandlersV1 {
  pointerDown(event: PointerEvent, target: HTMLElement): void;
  pointerMove(event: PointerEvent): void;
  pointerUp(event: PointerEvent): void;
}

export interface GameInputControllerOptionsV1 {
  readonly canvas: HTMLCanvasElement;
  readonly runtime: P0020PlayableRuntimeV1;
  readonly getState: () => GameInputStateV1;
  readonly onAutomaticInput: (scheme: ActiveControlSchemeV1) => void;
  readonly onInputKind: (kind: Exclude<LastControlInputV1, null>) => void;
  readonly onMenu: () => void;
  readonly onPointerLockFailure: () => void;
}

function freeze<Value>(value: Value): Readonly<Value> {
  return Object.freeze(value);
}

function point(event: PointerEvent): PointV1 {
  return freeze({ x: event.clientX, y: event.clientY });
}

function distance(left: PointV1, right: PointV1): number {
  return Math.hypot(left.x - right.x, left.y - right.y);
}

function isEditableTarget(target: EventTarget | null): boolean {
  return target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement;
}

export function createGameInputControllerV1(options: GameInputControllerOptionsV1): GameInputControllerV1 {
  const pressed = new Set<string>();
  const consumedKeys = new Set<string>();
  const consumedPointers = new Set<number>();
  const movePointers = new Map<number, PointV1>();
  const moveOrigins = new Map<number, PointV1>();
  const lookPointers = new Map<number, PointV1>();
  let consumedKeyboardSequences = 0;
  let consumedPointerSequences = 0;
  let sprintLatched = false;
  let pitchDegrees = 18;
  let previousPinchDistance: number | null = null;
  let mouseDrag: MouseDragV1 | null = null;
  let disposed = false;

  const reset = (reason: string): void => {
    void reason;
    pressed.clear();
    movePointers.clear();
    moveOrigins.clear();
    lookPointers.clear();
    previousPinchDistance = null;
    mouseDrag = null;
    options.runtime.resetContinuousInput();
  };

  const updateKeyboardMovement = (): void => {
    const forward = Number(pressed.has("KeyW") || pressed.has("ArrowUp")) - Number(pressed.has("KeyS") || pressed.has("ArrowDown"));
    const right = Number(pressed.has("KeyD") || pressed.has("ArrowRight")) - Number(pressed.has("KeyA") || pressed.has("ArrowLeft"));
    const sprint = sprintLatched || pressed.has("ShiftLeft") || pressed.has("ShiftRight");
    options.runtime.setMovement(forward, right, sprint);
  };

  const shouldConsumeForAutomaticSwitch = (target: ActiveControlSchemeV1): boolean => {
    const state = options.getState();
    return !state.blocked && state.preference === "auto" && state.activeScheme !== target;
  };

  const keyDown = (event: KeyboardEvent): void => {
    if (event.defaultPrevented || !event.isTrusted || isEditableTarget(event.target)) return;
    if (event.code === "Escape") {
      reset("escape");
      if (document.pointerLockElement === options.canvas) document.exitPointerLock();
      else options.onMenu();
      event.preventDefault();
      return;
    }
    if (options.getState().blocked) return;
    if (!MODIFIER_CODES.has(event.code) && shouldConsumeForAutomaticSwitch("keyboard_mouse")) {
      consumedKeys.add(event.code);
      consumedKeyboardSequences += 1;
      reset("automatic-keyboard-switch");
      options.onAutomaticInput("keyboard_mouse");
      event.preventDefault();
      return;
    }
    if (consumedKeys.has(event.code)) {
      event.preventDefault();
      return;
    }
    options.onInputKind("keyboard");
    if (event.code === "Space" && !event.repeat) options.runtime.requestJump();
    if (event.code === "KeyB" && !event.repeat) {
      const current = options.runtime.snapshot().build.interactionMode;
      options.runtime.setInteractionMode(current === "build" ? "explore" : "build");
    }
    const block = BLOCK_BY_CODE[event.code];
    if (block !== undefined && !event.repeat) options.runtime.selectBlock(block);
    if (MOVEMENT_CODES.has(event.code) || MODIFIER_CODES.has(event.code)) {
      pressed.add(event.code);
      updateKeyboardMovement();
    }
    if (event.code === "Space" || MOVEMENT_CODES.has(event.code)) event.preventDefault();
  };

  const keyUp = (event: KeyboardEvent): void => {
    if (consumedKeys.delete(event.code)) {
      event.preventDefault();
      return;
    }
    if (pressed.delete(event.code)) updateKeyboardMovement();
  };

  const wheel = (event: WheelEvent): void => {
    if (options.getState().blocked) return;
    options.onInputKind("mouse");
    if (event.shiftKey) {
      const selected = options.runtime.snapshot().build.selectedBlockId;
      const nextIndex = (selected - 1 + (event.deltaY > 0 ? 1 : -1) + PLACEABLE_BLOCK_IDS_V1.length) % PLACEABLE_BLOCK_IDS_V1.length;
      const next = PLACEABLE_BLOCK_IDS_V1[nextIndex];
      if (next !== undefined) options.runtime.selectBlock(next);
    } else {
      options.runtime.zoomBy(Math.sign(event.deltaY) * 0.4);
    }
    event.preventDefault();
  };

  const mouseDown = (event: PointerEvent): void => {
    if (!event.isTrusted || event.pointerType !== "mouse" || options.getState().blocked) return;
    if (shouldConsumeForAutomaticSwitch("keyboard_mouse")) {
      consumedPointers.add(event.pointerId);
      consumedPointerSequences += 1;
      reset("automatic-mouse-switch");
      options.onAutomaticInput("keyboard_mouse");
      event.preventDefault();
      return;
    }
    options.onInputKind("mouse");
    if (document.pointerLockElement === options.canvas) {
      if (event.button === 0) void options.runtime.requestPlace();
      if (event.button === 2) void options.runtime.requestRemove();
      return;
    }
    mouseDrag = { button: event.button, dragged: false, pointerId: event.pointerId, x: event.clientX, y: event.clientY };
    options.canvas.setPointerCapture(event.pointerId);
  };

  const mouseMove = (event: PointerEvent): void => {
    if (event.pointerType !== "mouse" || options.getState().blocked) return;
    options.onInputKind("mouse");
    const deltaX = document.pointerLockElement === options.canvas ? event.movementX : mouseDrag?.pointerId === event.pointerId ? event.clientX - mouseDrag.x : 0;
    const deltaY = document.pointerLockElement === options.canvas ? event.movementY : mouseDrag?.pointerId === event.pointerId ? event.clientY - mouseDrag.y : 0;
    if (mouseDrag !== null && mouseDrag.pointerId === event.pointerId) {
      mouseDrag.dragged ||= Math.hypot(deltaX, deltaY) > 2;
      mouseDrag.x = event.clientX;
      mouseDrag.y = event.clientY;
    }
    if (deltaX === 0 && deltaY === 0) return;
    options.runtime.orbitBy(deltaX * 0.006);
    pitchDegrees = Math.max(-55, Math.min(65, pitchDegrees - deltaY * 0.3));
    options.runtime.setPitchDegrees(pitchDegrees);
  };

  const pointerEnd = (event: PointerEvent): void => {
    if (consumedPointers.delete(event.pointerId)) event.preventDefault();
    if (mouseDrag?.pointerId !== event.pointerId) return;
    const completed = mouseDrag;
    mouseDrag = null;
    if (!completed.dragged && completed.button === 0) void options.runtime.requestPlace();
    if (!completed.dragged && completed.button === 2) void options.runtime.requestRemove();
  };

  const contextMenu = (event: MouseEvent): void => {
    if (event.target === options.canvas) event.preventDefault();
  };

  const blur = (): void => reset("window-blur");
  const visibility = (): void => {
    if (document.visibilityState !== "visible") reset("document-hidden");
  };
  const pointerLockChange = (): void => {
    if (document.pointerLockElement !== options.canvas) reset("pointer-lock-released");
  };

  const beginTouch = (event: PointerEvent, target: HTMLElement, region: "look" | "move"): boolean => {
    if (!event.isTrusted || event.pointerType !== "touch" || options.getState().blocked) return false;
    if (shouldConsumeForAutomaticSwitch("touch")) {
      consumedPointers.add(event.pointerId);
      consumedPointerSequences += 1;
      reset("automatic-touch-switch");
      options.onAutomaticInput("touch");
      event.preventDefault();
      return false;
    }
    options.onInputKind("touch");
    const current = point(event);
    target.setPointerCapture(event.pointerId);
    if (region === "move") {
      moveOrigins.set(event.pointerId, current);
      movePointers.set(event.pointerId, current);
    } else {
      lookPointers.set(event.pointerId, current);
      if (lookPointers.size === 2) {
        const [left, right] = [...lookPointers.values()];
        previousPinchDistance = left === undefined || right === undefined ? null : distance(left, right);
      }
    }
    event.preventDefault();
    return true;
  };

  const moveTouch = (event: PointerEvent, region: "look" | "move"): void => {
    if (consumedPointers.has(event.pointerId)) {
      event.preventDefault();
      return;
    }
    if (event.pointerType !== "touch" || options.getState().blocked) return;
    const current = point(event);
    if (region === "move") {
      const origin = moveOrigins.get(event.pointerId);
      if (origin === undefined || !movePointers.has(event.pointerId)) return;
      movePointers.set(event.pointerId, current);
      options.runtime.setMovement(
        Math.max(-1, Math.min(1, (origin.y - current.y) / 52)),
        Math.max(-1, Math.min(1, (current.x - origin.x) / 52)),
        sprintLatched,
      );
    } else {
      const previous = lookPointers.get(event.pointerId);
      if (previous === undefined) return;
      lookPointers.set(event.pointerId, current);
      if (lookPointers.size === 1) {
        options.runtime.orbitBy((current.x - previous.x) * 0.006);
        pitchDegrees = Math.max(-55, Math.min(65, pitchDegrees - (current.y - previous.y) * 0.3));
        options.runtime.setPitchDegrees(pitchDegrees);
      } else if (lookPointers.size === 2) {
        const [left, right] = [...lookPointers.values()];
        const nextDistance = left === undefined || right === undefined ? null : distance(left, right);
        if (nextDistance !== null && previousPinchDistance !== null) {
          options.runtime.zoomBy((previousPinchDistance - nextDistance) * 0.012);
        }
        previousPinchDistance = nextDistance;
      }
    }
    event.preventDefault();
  };

  const endTouch = (event: PointerEvent, region: "look" | "move"): void => {
    if (consumedPointers.delete(event.pointerId)) {
      event.preventDefault();
      return;
    }
    if (region === "move") {
      movePointers.delete(event.pointerId);
      moveOrigins.delete(event.pointerId);
      if (movePointers.size === 0) options.runtime.setMovement(0, 0, sprintLatched);
    } else {
      lookPointers.delete(event.pointerId);
      previousPinchDistance = null;
      if (lookPointers.size === 2) {
        const [left, right] = [...lookPointers.values()];
        previousPinchDistance = left === undefined || right === undefined ? null : distance(left, right);
      }
    }
    event.preventDefault();
  };

  const handlers = (region: "look" | "move"): TouchRegionHandlersV1 => freeze({
    pointerDown(event: PointerEvent, target: HTMLElement): void { beginTouch(event, target, region); },
    pointerMove(event: PointerEvent): void { moveTouch(event, region); },
    pointerUp(event: PointerEvent): void { endTouch(event, region); },
  });

  window.addEventListener("keydown", keyDown);
  window.addEventListener("keyup", keyUp);
  window.addEventListener("blur", blur);
  document.addEventListener("visibilitychange", visibility);
  document.addEventListener("pointerlockchange", pointerLockChange);
  options.canvas.addEventListener("pointerdown", mouseDown);
  options.canvas.addEventListener("pointermove", mouseMove);
  options.canvas.addEventListener("pointerup", pointerEnd);
  options.canvas.addEventListener("pointercancel", pointerEnd);
  options.canvas.addEventListener("contextmenu", contextMenu);
  options.canvas.addEventListener("wheel", wheel, { passive: false });

  return freeze({
    moveRegionHandlers: handlers("move"),
    lookRegionHandlers: handlers("look"),
    async requestPointerLock(): Promise<boolean> {
      try {
        await options.canvas.requestPointerLock();
        return document.pointerLockElement === options.canvas;
      } catch {
        options.onPointerLockFailure();
        return false;
      }
    },
    reset,
    setSprintLatched(enabled: boolean): void {
      sprintLatched = enabled;
      updateKeyboardMovement();
    },
    snapshot(): GameInputSnapshotV1 {
      return freeze({
        consumedKeyboardSequences,
        consumedPointerSequences,
        lookPointers: lookPointers.size,
        movePointers: movePointers.size,
        pointerLocked: document.pointerLockElement === options.canvas,
      });
    },
    dispose(): void {
      if (disposed) return;
      disposed = true;
      reset("dispose");
      window.removeEventListener("keydown", keyDown);
      window.removeEventListener("keyup", keyUp);
      window.removeEventListener("blur", blur);
      document.removeEventListener("visibilitychange", visibility);
      document.removeEventListener("pointerlockchange", pointerLockChange);
      options.canvas.removeEventListener("pointerdown", mouseDown);
      options.canvas.removeEventListener("pointermove", mouseMove);
      options.canvas.removeEventListener("pointerup", pointerEnd);
      options.canvas.removeEventListener("pointercancel", pointerEnd);
      options.canvas.removeEventListener("contextmenu", contextMenu);
      options.canvas.removeEventListener("wheel", wheel);
    },
  });
}
